import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, slideInBottom } from '../../styles/animations';

// Tableau des images à faire défiler
const images = [
    '/IMG_0593-1024x768-2560x1200.jpg',
    'IMG_0554-1024x768-2560x1200.jpg', // Remplacez par le chemin de votre deuxième image
    '20140224_0683-1024x768-2560x1200.jpg', // Remplacez par le chemin de votre troisième image
];

// Animation de fondu
const fade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const HeroSection = styled.section`
  min-height: 85vh;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  overflow: hidden;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    animation: ${fade} 2s ease-in-out forwards; // Animation de fondu
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  margin-left: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.light};
  z-index: 2;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin-left: ${props => props.theme.spacing.md};
    padding: 0 ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    margin-left: 0;
    padding: 0 ${props => props.theme.spacing.sm};
    text-align: center;
  }
  
  h1 {
    color: ${props => props.theme.colors.light};
    margin-top: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    font-size: clamp(1.4rem, 2.2vw, 2.4rem);
    line-height: 1.25;
  }

  p {
    font-size: clamp(0.95rem, 1.4vw, ${props => props.theme.fontSizes.large});
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.light};
    max-width: 90%;
    line-height: 1.6;

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
      max-width: 100%;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  animation: ${slideInBottom} 1s ease-out 0.5s forwards;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: background-color ${props => props.theme.transitions.fast};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    color: ${props => props.theme.colors.light};
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: ${props => props.theme.colors.light};
  font-weight: 600;
  padding: 0.9rem 1.9rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  border: 2px solid ${props => props.theme.colors.light};
  transition: all ${props => props.theme.transitions.fast};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.light};
    color: ${props => props.theme.colors.primary};
  }
`;

const Hero = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        // Définir un intervalle pour changer l'image toutes les 5 secondes
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 3500); // Change d'image toutes les 3.5 secondes

        // Nettoyer l'intervalle lors du démontage du composant
        return () => clearInterval(interval);
    }, []);

    return (
        <HeroSection>
            <HeroBackground>
                <img src={images[currentImageIndex]} alt="Terrasse en bois" />
            </HeroBackground>

            <div className="container">
                <HeroContent>
                    <h1>Votre terrasse sur-mesure à Nantes<br />Bois, composite & dalles — pose pro et durable</h1>
                    <p>
                        Spécialistes de la terrasse en bois sur la région nantaise et le littoral, nous concevons et posons des terrasses élégantes, résistantes et faciles d'entretien. Du diagnostic au chantier « prêt à profiter », votre projet est entre de bonnes mains.
                    </p>
                    <ButtonGroup>
                        <PrimaryButton to="/realisations">Découvrir nos réalisations</PrimaryButton>
                        <SecondaryButton to="/contact">Demander un devis</SecondaryButton>
                    </ButtonGroup>
                </HeroContent>
            </div>
        </HeroSection>
    );
};

export default Hero;